'use client';
import React from 'react';
import { AutocompleteModule, QueryScore } from '../Modules';
import { SearchItem } from '@benzinga/search-modules';
import { SafePromiseCancelableReturn } from '@benzinga/safe-await';

export interface autocompleteDataHookParams<
  M extends AutocompleteModule<T>,
  T = M extends AutocompleteModule<infer T2> ? T2 : never,
> {
  autocompleteModules: M[];
  queryLimit?: number;
}

export const useCancelableAutocomplete = <
  M extends AutocompleteModule<T>,
  T = M extends AutocompleteModule<infer T2> ? T2 : never,
>(
  params: autocompleteDataHookParams<M, T>,
) => {
  const defaultAutocompleteParams: Required<autocompleteDataHookParams<M, T>> = {
    autocompleteModules: [],
    queryLimit: 10,
  };
  const { autocompleteModules, queryLimit } = {
    ...defaultAutocompleteParams,
    ...params,
  };
  const autocompleteModulesRef = React.useRef<M[]>(autocompleteModules);
  autocompleteModulesRef.current = autocompleteModules;
  const searchCancelable = React.useCallback(
    (searchTerm = '', selectedItems: SearchItem<T>[] = []): SafePromiseCancelableReturn<SearchItem<T>[]> => {
      let queries: SafePromiseCancelableReturn<[SearchItem<T>, QueryScore][]>[] = [];
      const specificModule = autocompleteModulesRef.current.find(
        module => module.specifier && searchTerm.startsWith(module.specifier),
      );
      if (specificModule && specificModule.specifier) {
        queries = [specificModule.query(searchTerm.slice(specificModule.specifier.length), queryLimit, selectedItems)];
      } else {
        queries = autocompleteModulesRef.current.map(module => module.query(searchTerm, queryLimit, selectedItems));
      }
      return {
        cancel: () => {
          queries.forEach(q => q.cancel());
        },
        promise: Promise.all(queries.map(query => query.promise)).then(results => {
          const finalResults: [SearchItem<T>, QueryScore][][] = [];
          for (const result of results) {
            if (result.canceled) {
              return { canceled: true };
            } else if (result.err) {
              return { canceled: false, err: result.err };
            } else {
              finalResults.push(result.ok);
            }
          }

          return {
            canceled: false,
            ok: fillEvenlyWithScore(finalResults, queryLimit).map(q => q[0]),
          };
        }),
      };
    },
    [queryLimit],
  );

  return { search: searchCancelable };
};

export const useAutocomplete = <
  M extends AutocompleteModule<T>,
  T = M extends AutocompleteModule<infer T2> ? T2 : never,
>(
  params: autocompleteDataHookParams<M, T>,
) => {
  const { search: searchCancelable } = useCancelableAutocomplete(params);
  const search = React.useCallback(
    async (searchTerm = '', selectedItems: SearchItem<T>[] = []): Promise<SearchItem<T>[] | undefined> => {
      return searchCancelable(searchTerm, selectedItems).promise.then(result => {
        if (result.canceled || result.err) {
          return undefined;
        }
        return result.ok;
      });
    },
    [searchCancelable],
  );
  return { search };
};

export const useStatefulAutocomplete = <
  M extends AutocompleteModule<T>,
  T = M extends AutocompleteModule<infer T2> ? T2 : never,
>(
  params: autocompleteDataHookParams<M, T>,
) => {
  const [searchResults, setSearchResults] = React.useState<SearchItem<T>[]>([]);
  const [searchResultsReady, setSearchResultsReady] = React.useState<boolean>(true);
  const currentRequest = React.useRef<SafePromiseCancelableReturn<SearchItem<T>[]> | undefined>(undefined);
  const { search: searchCancelable } = useCancelableAutocomplete(params);

  const searchState = React.useCallback(
    async (searchTerm: string | undefined, selectedItems: SearchItem<T>[] = []) => {
      setSearchResultsReady(false);
      if (currentRequest.current) {
        currentRequest.current.cancel();
        currentRequest.current = undefined;
      }
      if (searchTerm == null) {
        setSearchResults([]);
        setSearchResultsReady(true);
        return;
      }
      currentRequest.current = searchCancelable(searchTerm, selectedItems);
      const searchResult = await currentRequest.current.promise;
      if (!searchResult.canceled) {
        currentRequest.current = undefined;
        if (searchResult.ok) {
          setSearchResults(searchResult.ok);
        }
        setSearchResultsReady(true);
      }
    },
    [searchCancelable],
  );

  return {
    search: searchState,
    searchResults,
    searchResultsReady,
  };
};

// simply put it takes in multiple arrays and simply trys to take as much out of them to fit the amount that is required
const fillEvenlyWithScore = <T>(items: [T, QueryScore][][], maxAmount: number): [T, QueryScore][] => {
  const newArray: [T, QueryScore][][][] = Array(QueryScore.Priority + 1)
    .fill(null)
    .map(() =>
      Array(items.length)
        .fill(null)
        .map(() => []),
    ); //Array[Priority][ItemType][Item]

  items.map(item => item.sort((a, b) => b[1] - a[1])); // sort on score for priority

  const itemsMaxAmount = items.reduce((acc, i) => acc + i.length, 0);
  maxAmount = maxAmount > itemsMaxAmount ? itemsMaxAmount : maxAmount;
  let currentPriority = QueryScore.Priority;
  let addedThisIteration = true;
  const itemTypeIndex = new Array(items.length).fill(0);

  for (let i = 0, currentAmount = 0; currentAmount < maxAmount; i++) {
    const itemType = i % items.length;
    if (itemType === 0) {
      if (!addedThisIteration) {
        currentPriority--;
      }
      addedThisIteration = false;
    }
    if (itemTypeIndex[itemType] < items[itemType].length) {
      const item = items[itemType][itemTypeIndex[itemType]];
      if (item[1] === currentPriority) {
        newArray[currentPriority][itemType].push(item);
        addedThisIteration = true;
        currentAmount++;
        itemTypeIndex[itemType]++;
      }
    }
  }
  return newArray.reverse().flat().flat();
};
